import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import {
  ColorSchemeEnum,
  useTheme
} from '../../../../../../../app/hooks/useTheme/useTheme';
import { useToastNotification } from '../../../../../../../common/hooks/useToastNotification';
import { buildEmbedUrl, getTargetOrigin } from './buildEmbedUrl';
import { UseVymirConfiguratorOptions } from './useVymirConfigurator.types';

function useVymirConfigurator({
  config,
  onModelExported
}: UseVymirConfiguratorOptions) {
  const [isOpen, setIsOpen] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const onModelExportedRef = useRef(onModelExported);
  onModelExportedRef.current = onModelExported;
  const { theme } = useTheme();
  const { showToastNotification } = useToastNotification({
    appearance: 'error',
    autoDismiss: true
  });

  const vymirTheme = theme === ColorSchemeEnum.DARK ? 'dark' : 'light';
  const embedUrl = config ? buildEmbedUrl(config, vymirTheme) : '';
  const targetOrigin = config ? getTargetOrigin(config) : '';

  const close = useCallback(() => {
    setIsOpen(false);
    setIsReady(false);
  }, []);

  const open = useCallback(() => {
    if (!config) return;
    setIsOpen(true);
  }, [config]);

  useLayoutEffect(() => {
    if (!isOpen || !config) return;

    const handler = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (event.origin !== targetOrigin) return;

      const data = event.data;
      if (!data || data.version !== '1.0' || !data.type?.startsWith('vymir:'))
        return;

      if (data.type === 'vymir:ready') {
        setIsReady(true);
        iframeRef.current?.contentWindow?.postMessage(
          { version: '1.0', type: 'vymir:set-theme', theme: vymirTheme },
          targetOrigin
        );
      } else if (data.type === 'vymir:export-glb-result') {
        const blob = new Blob([data.data], { type: 'model/gltf-binary' });
        const fileUrl = URL.createObjectURL(blob);
        onModelExportedRef.current({
          file: fileUrl,
          name: `${config.slug}.glb`
        });
        close();
      } else if (data.type === 'vymir:export-glb-error') {
        showToastNotification(
          data.message || 'Failed to export model from configurator'
        );
        close();
      }
    };

    window.addEventListener('message', handler);
    return () => {
      window.removeEventListener('message', handler);
    };
  }, [isOpen, config, targetOrigin, vymirTheme, close, showToastNotification]);

  return { isOpen, isReady, iframeRef, embedUrl, open, close };
}

export default useVymirConfigurator;
